import React, {
    useState, useCallback, useRef, useMemo,
} from 'react';
import { filter } from 'lodash';

import Slide from '@material-ui/core/Slide';
import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Utils from '../services/UtilsService';
import { doveGray } from '../constants/Colors';

const ModalContext = React.createContext({
    showModal: () => {},
    hideModal: () => {},
    showModalWithDelay: () => {},
});

export const ModalProvider = ModalContext.Provider;

export const ModalConsumer = ModalContext.Consumer;

export const HIDE_TRANSITION_TIME = 200;
export const DELAY_TO_OPEN_NEXT_DIALOG = 500;
const styles = {
    modalRoot: {
        overflow: 'auto',
    },
    container: {
        height: ({ isAutoHeight }) => (isAutoHeight ? 'auto' : '100%'),
        display: 'flex',
        alignItems: 'center',
    },
    root: {
        width: '60%',
    },
    closeButton: {
        position: 'absolute',
        right: '0',
        top: '0',
        zIndex: 1,

        '& svg': {
            color: doveGray,
        },
    },
    modal: {
        borderRadius: '12px',
        '&.overflow': {
            overflow: 'initial',
        },
        overflow: 'initial',
    },
};

const useStyles = makeStyles(() => styles);

const Transition = React.forwardRef((props, ref) => (
    <Slide ref={ref} direction='down' {...props} />
));

const ModalPlaceholder = (props) => {
    const { modals, onClose } = props;
    let isAutoHeight = false;
    if (modals.length) {
        isAutoHeight = modals[0].isAutoHeight;
    }
    const classes = useStyles({ isAutoHeight });

    return (modals || []).map(({
        id,
        body,
        open,
        rootClassName = '',
        className,
        closeButtonClassName = '',
        hasCloseButton = true,
        withOverflow,
        width,
        size = 'sm',
        zIndex,
        fullWidth = false,
        onCloseHook = () => {},
        allowClose = true,
        transition,
    }, index) => (
        <Dialog
            key={index}
            open={open}
            classes={{
                root: Utils.getClassName([
                    classes.modalRoot,
                    rootClassName,
                ]),
                container: classes.container,
                paper: Utils.getClassName([
                    classes.modal,
                    className,
                    withOverflow ? 'overflow' : '',
                ]),
            }}
            style={{
                ...(zIndex ? { zIndex } : {}),
            }}
            maxWidth={width}
            fullWidth={fullWidth}
            TransitionComponent={transition || Transition}
            keepMounted
            onClose={() => {
                if (allowClose) {
                    onCloseHook();
                    onClose(id);
                }
            }}
            disableScrollLock
            aria-labelledby='notice-modal-slide-title'
            aria-describedby='notice-modal-slide-description'>
            { body }
            { hasCloseButton
                && <div className={closeButtonClassName || classes.closeButton}>
                    <IconButton
                        aria-label='close'
                        onClick={() => {
                            if (allowClose) {
                                onCloseHook({ source: 'closeIcon' });
                                onClose(id);
                            }
                        }}>
                        <CloseIcon/>
                    </IconButton>
                </div>
            }
        </Dialog>
    ));
};

export const AppWithModal = ({ children }) => {
    const [modals, setModals] = useState([]);
    const currentModalId = useRef(1);

    const removeModal = useCallback((modalId) => {
        if (modalId) {
            setModals((currentModals) => filter(currentModals, ({ id }) => id !== modalId));
        }
    }, []);

    const hideModal = useCallback((modalId, delay) => {
        setModals((currentModals) => {
            const index = (currentModals || []).findIndex(({ id }) => id === modalId);

            if (index > -1) {
                return ((currentModals || []).map((currentModal) => {
                    if (currentModal.id === modalId) {
                        if (currentModal?.onClose) {
                            currentModal.onClose();
                        }

                        return {
                            ...currentModal,
                            open: false,
                        };
                    }

                    return currentModal;
                }));
            }

            return currentModals;
        });

        setTimeout(() => removeModal(modalId), delay || HIDE_TRANSITION_TIME);
    }, [removeModal]);

    const showModal = useCallback((modal) => {
        const id = currentModalId.current;

        setModals((currentModals) => [...currentModals, { ...modal, open: true, id }]);
        currentModalId.current += 1;

        return id;
    }, []);

    const showModalWithDelay = useCallback(async (modal, delay = DELAY_TO_OPEN_NEXT_DIALOG) => new Promise(
        // eslint-disable-next-line no-promise-executor-return
        (resolve) => setTimeout(
            () => { resolve(showModal(modal)); },
            delay,
        ),
    ), []);

    const contextValue = useMemo(() => ({
        showModal,
        hideModal,
        showModalWithDelay,
        delayToOpenNextDialog: DELAY_TO_OPEN_NEXT_DIALOG,
    }), [
        showModal,
        hideModal,
        showModalWithDelay,
    ]);

    return (
        <ModalProvider value={contextValue}>
            <ModalPlaceholder
                modals={modals}
                onClose={(id) => hideModal(id)}
            />
            {children}
        </ModalProvider>
    );
};

export default ModalContext;
